import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { Collapse, Nav, OverlayTrigger, Tooltip } from "react-bootstrap";
import Logo from "../../assets/images/Logo-Portal_Access.png";
import api from "../../config/URL";
import { PiBuildings, PiBookOpenText } from "react-icons/pi";
import { GiExitDoor } from "react-icons/gi";
import { HiOutlineUserGroup } from "react-icons/hi2";
import {
  TbUserSearch,
  TbCalendarTime,
  TbFolderCog,
  TbMessageCode,
} from "react-icons/tb";
import { LiaFileInvoiceDollarSolid, LiaUserEditSolid } from "react-icons/lia";
import { VscReferences } from "react-icons/vsc";
import { BsFileEarmarkRichtext } from "react-icons/bs";
import { GrUserSettings } from "react-icons/gr";
import { RiDashboardLine } from "react-icons/ri";
import { IoIosAdd, IoIosRemove } from "react-icons/io";

const iconMapping = {
  "Centre Management": <PiBuildings />,
  "Course Management": <PiBookOpenText />,
  "Lead Management": <GiExitDoor />,
  Staffing: <HiOutlineUserGroup />,
  "Student Management": <TbUserSearch />,
  Schedule: <TbCalendarTime />,
  "Folder Management": <TbFolderCog />,
  "Invoice and Payment": <LiaFileInvoiceDollarSolid />,
  "Referral Management": <VscReferences />,
  "Report Management": <BsFileEarmarkRichtext />,
  "Content Management": <LiaUserEditSolid />,
  Messaging: <TbMessageCode />,
  Settings: <GrUserSettings />,
};

function Sidebar({ leadCounts, messageCounts = { newMessageCount: 0 } }) {
  const [menuItems, setMenuItems] = useState([]);
  const [data, setData] = useState({});
  const [openMenus, setOpenMenus] = useState({});
  const location = useLocation();
  const storedScreens = useMemo(
    () => JSON.parse(localStorage.getItem("screens") || "{}"),
    [],
  );
  const messageCount = Number(messageCounts?.newMessageCount ?? 0);

  const masterSettingAccess = useMemo(() => {
    return (
      storedScreens.absentReasonSettingIndex ||
      storedScreens.taxSettingIndex ||
      storedScreens.raceSettingIndex ||
      storedScreens.countrySettingIndex ||
      storedScreens.idTypeSettingIndex ||
      storedScreens.batchtimeSettingIndex
    );
  }, [storedScreens]);

  const hrSettingAccess = useMemo(() => {
    return (
      storedScreens.shgSettingIndex ||
      storedScreens.leaveSettingIndex ||
      storedScreens.salarySettingIndex
    );
  }, [storedScreens]);

  // console.log("Storage is ::", storedScreens.smsMessageIndex);
  const userRole = localStorage.getItem("userName"); // e.g. SMS_ADMIN
  const isAdmin = userRole === "SMS_ADMIN";

  // Fetch initial data
  useEffect(() => {
    const getData = async () => {
      try {
        const response = await api.get(`/getAllHeaderSavePublish`);
        setData(response.data);
      } catch (error) {
        console.error("Error Fetching Data: " + error.message);
      }
    };
    getData();
  }, []);

  useEffect(() => {
    const updatedMenuItems = [
      {
        title: "Centre Management",
        subMenus: [
          {
            title: "Centre Listing",
            path: "/center",
            access: storedScreens.centerListingIndex,
          },
        ],
      },
      {
        title: "Course Management",
        subMenus: [
          {
            title: "Subject",
            path: "/subject",
            access: storedScreens.subjectIndex,
          },
          { title: "Level", path: "/level", access: storedScreens.levelIndex },
          {
            title: "Course",
            path: "/course",
            access: storedScreens.courseIndex,
          },
          { title: "Class", path: "/class", access: storedScreens.classIndex },
        ],
      },
      {
        title: "Lead Management",
        totalCount: leadCounts.newLeadsCount + leadCounts.newContactsCount,
        subMenus: [
          {
            title: "Lead Listing",
            path: "lead/lead",
            access: storedScreens.leadListingIndex,
            count: leadCounts.newLeadsCount,
          },
          {
            title: "Contacts",
            path: "/lead/contacted",
            access: storedScreens.enrollmentIndex,
            count: leadCounts.newContactsCount,
          },
        ],
      },
      {
        title: "Staffing",
        subMenus: [
          { title: "Staff", path: "/staff", access: storedScreens.staffIndex },
          {
            title: "Teacher",
            path: "/teacher",
            access: storedScreens.teacherIndex,
          },
          {
            title: "Attendance",
            path: "/staffing/attendance",
            access: storedScreens.staffAttendanceIndex,
          },
          {
            title: "Check Attendance",
            path: "/staffing/check",
            access: storedScreens.checkAttendanceIndex,
          },
          {
            title: "Leave",
            path: "/leaveadmin",
            access: storedScreens.leaveAdminIndex,
          },
          {
            title: "Leave Request",
            path: "/leave",
            access: storedScreens.leaveIndex,
          },
          {
            title: "Holiday",
            path: "/holiday",
            access: storedScreens.holidayIndex,
          },
          {
            title: "Deduction",
            path: "/deduction",
            access: storedScreens.deductionIndex,
          },
          {
            title: "Payroll",
            path: "/payrolladmin",
            access: storedScreens.payrollIndex,
          },
          {
            title: "Payslip",
            path: "/employeepayslip",
            access: storedScreens.payslipIndex,
          },
          {
            title: "Freelancer Invoice",
            path: "/freelancerPayslip",
            access: storedScreens.freeLancerIndex,
          },
          {
            title: "Role & Matrix",
            path: "/role/add",
            access: storedScreens.rolesMatrixIndex,
          },
        ],
      },
      {
        title: "Student Management",
        subMenus: [
          {
            title: "Student Listing",
            path: "/student",
            access: storedScreens.studentListingIndex,
          },
          {
            title: "Attendance Taking",
            path: "/attendance",
            access: storedScreens.attendanceIndex,
          },
          {
            title: "Makeup Class List",
            path: "/replaceclasslesson",
            access: storedScreens.replaceClassLessonListIndex,
          },
          {
            title: "End Class List",
            path: "/endClass",
            access: storedScreens.endClassIndex,
          },
          {
            title: "Change Class List",
            path: "/changeClass",
            access: storedScreens.changeClassIndex,
          },
          {
            title: "Transfer Out List",
            path: "/transferOut",
            access: storedScreens.transferOutIndex,
          },
          {
            title: "Suspend List",
            path: "/suspend",
            access: storedScreens.suspendIndex,
          },
          {
            title: "Graduate List",
            path: "/graduate",
            access: storedScreens.graduateIndex,
          },
          {
            title: "Withdraw List",
            path: "/withdraw",
            access: storedScreens.withdrawIndex,
          },
          // {
          //   title: "Student Slot Count Report",
          //   path: "/report/studentSlotCountReports",
          //   access: true,
          // },
          {
            title: "Enrollment Form",
            path: "/newStudentForm",
            access: true,
          },
          {
            title: "Student Report",
            path: "/report/studentReports",
            access: storedScreens.studentReportIndex,
          },
          {
            title: "Student Attendance Report",
            path: "/report/studentAttendanceReport",
            access: storedScreens.attendanceReportIndex,
          },
          {
            title: "Enrollment Report",
            path: "/report/studentEnrollmentReport",
            access: storedScreens.enrollmentReportIndex,
          },
          // {
          //   title: "Enrollment Report",
          //   path: "/report/enrollmentReports",
          //   access: true,
          // },
        ],
      },
      {
        title: "Folder Management",
        subMenus: [
          {
            title: "Create Folder",
            path: "/documentfile",
            access: storedScreens.documentListingCreate,
          },
          {
            title: "Folder List",
            path: "/document",
            access: storedScreens.documentFileIndex,
          },
        ],
      },
      // {
      //   title: "Schedule",
      //   subMenus: [
      //     // {
      //     //   title: "Time Schedule",
      //     //   path: "/scheduleReport",
      //     //   access: storedScreens.scheduleTeacherIndex,
      //     // },
      //     {
      //       title: "Calendar",
      //       path: "/calendar",
      //       access: storedScreens.scheduleTeacherIndex,
      //     },
      //   ],
      // },
      {
        title: "Invoice and Payment",
        subMenus: [
          {
            title: "Invoice",
            path: "/invoice",
            access: storedScreens.invoiceIndex,
          },
          {
            title: "Payment",
            path: "/payment",
            access: storedScreens.paymentIndex,
          },
          {
            title: "Credit Note History",
            path: "/creditNoteHistory",
            access: storedScreens.creditNoteHistoryIndex,
          },
          {
            title: "Revenue Report",
            path: "/report/salesRevenueReport",
            access: storedScreens.revenueReportIndex,
          },
          {
            title: "Deposit & Registration Fee",
            path: "/report/deposit-report",
            access: storedScreens.depositRegistrationBalanceReportIndex,
          },
        ],
      },
      {
        title: "Referral Management",
        subMenus: [
          {
            title: "Set Referral Fees",
            path: "/referalFees",
            access: storedScreens.referralFeesIndex,
          },
          {
            title: "Referral Report",
            path: "/report/referalRepots",
            access: storedScreens.referralReportIndex,
          },
          // {
          //   title: "Referral History",
          //   path: "/referalHistory",
          //   access: storedScreens.referralHistoryIndex,
          // },
        ],
      },
      // {
      //   title: "OLD Report",
      //   subMenus: [
      //     {
      //       title: "Attendance Report",
      //       path: "/report/attendance",
      //       access: storedScreens.attendanceReportIndex,
      //     },
      //     {
      //       title: "Revenue Report",
      //       path: "/report/revenue",
      //       access: storedScreens.assessmentReportIndex,
      //     },
      //     {
      //       title: "Enrollment Report",
      //       path: "/report/enrolment",
      //       access: storedScreens.enrollmentReportIndex,
      //     },
      //     {
      //       title:"Package Balance Report",
      //       path: "/report/packageBalanceReport",
      //       access: storedScreens.enrollmentReportIndex,
      //     },
      //     {
      //       title:"Yearly Payroll Report",
      //       path: "/report/yearlyPayrollReport",
      //       access: storedScreens.enrollmentReportIndex,
      //     },
      //     {
      //       title:"Package Balance Report",
      //       path: "/report/packageBalanceReport",
      //       access: storedScreens.enrollmentReportIndex,
      //     },
      //   ],
      // },
      {
        title: "Report Management",
        subMenus: [
          {
            title: "Package Balance Report",
            path: "/report/packageBalanceReport",
            access: storedScreens.packageBalanceReportIndex,
          },

          {
            title: "Forfeit Report",
            path: "/report/forfeit-report",
            access: storedScreens.forfeitWithdrawalReportIndex,
          },
          //     // {
          //     //   title: "Timetable Report",
          //     //   path: "/report/timetableReport",
          //     //   access: true,
          //     // },
        ],
      },
      {
        title: "Messaging",
        totalCount: messageCount,
        subMenus: [
          {
            title: "My Messages",
            path: "/messaging",
            access: storedScreens.smsMessageIndex,
            count: messageCount,
          },
          {
            title: "Other Messages",
            path: "/othermessaging",
            access: isAdmin,
          },
          {
            title: "School Announcement",
            path: "/sendNotification",
            access: storedScreens.schoolAnnouncementIndex,
          },
        ],
      },
      {
        title: "Settings",
        subMenus: [
          // { title: "Tax", path: "/tax", access: storedScreens.taxSettingIndex },
          // {
          //   title: "Race",
          //   path: "/race",
          //   access: storedScreens.raceSettingIndex,
          // },
          // {
          //   title: "Country & Nationality",
          //   path: "/country",
          //   access: storedScreens.countrySettingIndex,
          // },
          // { title: "SHG", path: "/shg", access: storedScreens.shgSettingIndex },
          // {
          //   title: "Batch Time",
          //   path: "/batchtime",
          //   access: storedScreens.batchtimeSettingIndex,
          // },
          // {
          //   title: "Leave Type",
          //   path: "/leavetype",
          //   access: storedScreens.leaveSettingIndex,
          // },
          // {
          //   title: "ID Type",
          //   path: "/idType",
          //   access: storedScreens.idTypeSettingIndex,
          // },
          // {
          //   title: "Salary Type",
          //   path: "/salarytype",
          //   access: storedScreens.salarySettingIndex,
          // },
          // {
          //   title: "Absent Reason",
          //   path: "/absentreason",
          //   access: storedScreens.salarySettingIndex,
          // },
          {
            title: "Master Settings",
            path: "/masterSettings",
            access: masterSettingAccess,
            // access: true,
          },
          {
            title: "HR Settings",
            path: "/hrSettings",
            access: hrSettingAccess,
            // access: true,
          },
          {
            title: "Email Template",
            path: "/emailTemplate",
            access: storedScreens.emailTemplateIndex,
          },
        ],
      },

      {
        title: "Content Management",
        subMenus: [
          {
            title: "Header & Footer",
            path: "/cms/header",
            access: storedScreens.headerIndex,
          },
          { title: "Home", path: "/cms/home", access: storedScreens.homeIndex },
          {
            title: "Blog",
            path: "/cms/cmsBlog",
            access: storedScreens.blogIndex,
          },
          {
            title: "Testimonial",
            path: "/cms/testimonial",
            access: storedScreens.testimonialIndex,
          },
          {
            title: "About",
            path: "/cms/aboutus",
            access: storedScreens.aboutIndex,
          },
          {
            title: "Courses",
            path: "/cms/CmsCourses",
            access: storedScreens.englishCourseIndex,
          },
          {
            title: "Teachers",
            path: "/cms/teacher",
            access: storedScreens.teacherSaveIndex,
          },
          {
            title: "Products",
            path: "/cms/products",
            access: storedScreens.productSaveIndex,
          },
          {
            title: "Products Items",
            path: "/cms/productsitem",
            access: storedScreens.productImageSaveIndex,
          },
          {
            title: "News & Updates",
            path: "/cms/newsupdate",
            access: storedScreens.newsUpdatesIndex,
          },
          {
            title: "Contact Us",
            path: "/cms/contact",
            access: storedScreens.contactUsIndex,
          },
          {
            title: "Enroll Terms",
            path: "/cms/enrollTerms",
            access: storedScreens.blogIndex,
          },
          {
            title: "Terms & Conditions",
            path: "/cms/termsAndCondition",
            access: storedScreens.blogIndex,
          },
          {
            title: "Privacy Policy",
            path: "/cms/privacyPolicyPage",
            access: storedScreens.blogIndex,
          },
        ],
      },
    ];
    setMenuItems(updatedMenuItems);
  }, [leadCounts, messageCounts]);

  // Handle menu click
  const handleMenuClick = (menuTitle) => {
    setOpenMenus((prev) => {
      const newOpenMenus = {};
      // If the clicked menu is already open, close it; otherwise, open it and close all others
      if (prev[menuTitle]) {
        newOpenMenus[menuTitle] = false;
      } else {
        newOpenMenus[menuTitle] = true;
      }
      return newOpenMenus;
    });
  };

  // Handle home click
  const handleHomeClick = () => {
    setOpenMenus({}); // Close all menus
  };

  // Reset menus when navigating to home
  useEffect(() => {
    if (location.pathname === "/") {
      setOpenMenus({}); // Ensure all menus are closed on home navigation
    }
  }, [location.pathname]);

  return (
    <div className="sidebar">
      <div className="logo-details">
        <span className="logo_name">
          <img src={Logo} alt="logo" width={100} className="img-fluid p-1" />
        </span>
      </div>
      <ul className="nav-links">
        <li>
          <NavLink
            to="/"
            onClick={handleHomeClick}
            className={({ isActive }) => (isActive ? "active activehover" : "")}
          >
            <OverlayTrigger
              placement="right"
              overlay={<Tooltip id="home-tooltip">Dashboard</Tooltip>}
            >
              <i className="homeIcons">
                <RiDashboardLine />
              </i>
            </OverlayTrigger>
            <span className="links_name text-center"> Dashboard</span>
          </NavLink>
        </li>
        {menuItems.map(
          (item, index) =>
            item.subMenus.some((subMenu) => subMenu.access) && (
              <li key={index}>
                <Nav.Link
                  onClick={() => handleMenuClick(item.title)}
                  className={openMenus[item.title] ? "active activehover" : ""}
                >
                  <div className="w-100 d-flex justify-content-between align-items-center">
                    <span className="d-flex align-items-center">
                      <OverlayTrigger
                        placement="right"
                        overlay={
                          <Tooltip id={`${item.title}-tooltip`}>
                            {item.title}
                          </Tooltip>
                        }
                      >
                        <span className="px-3">
                          {iconMapping[item.title] || "-"}
                        </span>
                      </OverlayTrigger>
                      <span className="links_name">{item.title}</span>
                    </span>
                    <span className="d-flex align-items-center pe-4">
                      {item.totalCount > 0 && (
                        <span
                          style={{
                            backgroundColor: "#eb862a",
                            color: "white",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: "0.9rem",
                            marginRight: "10px",
                          }}
                        >
                          {item.totalCount}
                        </span>
                      )}
                      {openMenus[item.title] ? (
                        <IoIosRemove size={24} />
                      ) : (
                        <IoIosAdd size={24} />
                      )}
                    </span>
                  </div>
                </Nav.Link>

                <Collapse in={openMenus[item.title]}>
                  <ul className="submenu">
                    {item.subMenus.map(
                      (subMenu, subIndex) =>
                        subMenu.access && (
                          <li key={subIndex}>
                            <NavLink
                              to={subMenu.path}
                              className={({ isActive }) =>
                                isActive ? "active-submenu" : ""
                              }
                            >
                              <OverlayTrigger
                                placement="right"
                                overlay={
                                  <Tooltip id={`${subMenu.title}-tooltip`}>
                                    {subMenu.title}
                                  </Tooltip>
                                }
                              >
                                <i
                                  className="bx bx-radio-circle-marked"
                                  style={{ fontWeight: "400" }}
                                ></i>
                              </OverlayTrigger>
                              <span className="links_name">
                                {subMenu.title}
                                {subMenu.count > 0 && (
                                  <span
                                    className="badge ms-1"
                                    style={{
                                      fontSize: "0.8rem",
                                      background: "#eb862a",
                                      borderRadius: "50%",
                                      padding: "4px 8px",
                                      color: "white",
                                      minWidth: "22px",
                                      textAlign: "center",
                                      display: "inline-block",
                                      lineHeight: "1.2",
                                    }}
                                  >
                                    {subMenu.count}
                                  </span>
                                )}
                              </span>
                            </NavLink>
                          </li>
                        ),
                    )}
                  </ul>
                </Collapse>
              </li>
            ),
        )}
      </ul>
    </div>
  );
}

export default Sidebar;
